import React from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Paper,
    IconButton,
    useTheme,
    useMediaQuery,
    Chip,
    Stack,
    Divider
} from '@mui/material';
import { ArrowBack, Cookie } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import SiteHeader from '../../components/SiteHeader';
import AppFooter from '../../components/AppFooter';

const LIGHT_PURPLE = '#A76FFF';
const VIBRANT_RED = '#FF5A76';

const WebCookiePolicyScreen: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <>
        <SEOHead
            title="Cookie Policy | Pop The Balloon"
            description="Learn which cookies Pop The Balloon uses on its website, why they are used and how you can manage your preferences."
            canonical="https://www.poptheballoonapp.com/cookie-policy"
            ogUrl="https://www.poptheballoonapp.com/cookie-policy"
        />
        <SiteHeader />
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton onClick={() => navigate(-1)} size="large" sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                    <Cookie sx={{ mr: 2, color: LIGHT_PURPLE }} />
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                        Cookie Policy
                    </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 4, fontStyle: 'italic' }}>
                    Last Updated: October 18, 2026
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        What Are Cookies?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Cookies are small text files that websites place on your device. Similar technologies, such as your browser's local storage, work in a similar way, and this policy covers them too.
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        Our Approach
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        This website gives information about the Pop The Balloon app. Visitors don't sign in here, and we don't set our own cookies for analytics, advertising or personalisation. This policy covers the website only. How the app uses your data, including the advertising and analytics tools inside it, is explained in our Privacy Policy.
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        Cookies and Similar Technologies on This Website
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            <Chip label="Necessary" size="small" color="success" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                Essential
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Our hosting provider may use strictly necessary technologies to deliver the website securely, for example to protect it from abuse. Our team's admin area keeps a sign-in session in the browser, but visitors never use it.
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            These are needed for the website to work and are always on.
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 3 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            <Chip label="Analytics" size="small" color="info" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                Google Analytics
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            We use Google Analytics in Consent Mode with analytics storage turned off, so it does not set or read analytics cookies on your device. Google may still receive limited information without cookies, such as that a page was viewed, when, and on what type of browser, which it uses for aggregate statistics.
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            No analytics cookies are stored on your device.
                        </Typography>
                    </Box>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        Managing Cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        You can manage cookies through your browser settings:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Block all cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Delete existing cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Set preferences for specific websites
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Blocking cookies won't stop this website from working.
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        Cookie Retention
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Session cookies are deleted when you close your browser. Persistent cookies remain until they expire or you delete them.
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        Updates to This Policy
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by updating the "Last Updated" date at the top of this policy.
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        Contact Us
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Email:</strong> support@poptheballoonapp.com
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                        <strong>Subject:</strong> Cookie Policy Inquiry
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate(-1)}
                        sx={{
                            bgcolor: LIGHT_PURPLE,
                            borderRadius: 5,
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: VIBRANT_RED,
                            },
                        }}
                    >
                        Back
                    </Button>
                </Box>
            </Paper>
        </Container>
        <AppFooter />
        </>
    );
};

export default WebCookiePolicyScreen;
